import React from "react";
import { useNavigate, useParams } from "react-router";
import { useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FormattedMessage } from 'react-intl';
import { Typography } from "ui-kit/Typography";
import { Button, CreateReleaseButton } from "ui-kit/Button";
import { Routes } from "constants/routes";
import { DeviceSize } from "constants/deviceSize";
import { LanguageSelect } from "./LanguageSelect";
import Notification from '../../assets/notification.svg'
import { SupportModal } from "./SupportModal";

const links = [
  {
    id: "",
    path: Routes.General,
    title: <FormattedMessage id="Header.Links.General" defaultMessage="General" description="General" />,
  },
  {
    id: "ai-compose",
    path: Routes.AICompose,
    title: <FormattedMessage id="Header.Links.AICompose" defaultMessage="AI Music" description="AI Compose" />,
  },
  {
    id: "analytics",
    path: Routes.Analytics,
    disabled: false,
    title: <FormattedMessage id="Header.Links.Analytics" defaultMessage="Analytics" description="Analytics" />,
  },
  {
    id: "smart-links",
    path: Routes.SmartLinks,
    disabled: false,
    title: <FormattedMessage id="Header.Links.SmartLinks" defaultMessage="Smart Links" description="Smart Links" />,
  },
  // {
  //   id: "loyalty",
  //   path: Routes.Loyalty,
  //   disabled: true,
  //   title: <FormattedMessage id="Header.Links.Loyalty" defaultMessage="Loyalty" description="Loyalty" />,
  // },
  {
    id: "finance",
    path: Routes.Finance,
    disabled: false,
    title: <FormattedMessage id="Header.Links.Finance" defaultMessage="Finance" description="Finance" />,
  },
  {
    id: "prices",
    path: Routes.Prices,
    disabled: false,
    title: <FormattedMessage id="Header.Links.Prices" defaultMessage="Prices" description="Prices" />,
  },
  {
    id: "hamburger",
    path: "#",
    disabled: false,
    title: null,
  },
  {
    id: "support",
    path: Routes.Support,
    disabled: false,
    title: <FormattedMessage id="Header.Links.Support" defaultMessage="Support" description="Support" />,
  }
];

// Отдельный порядок ссылок для мобильного нижнего бара:
// 1) General, 2) Analytics, 3) AI Music, 4) Prices, 5) Hamburger
const mobileLinks = [
  links.find(l => l.id === ""),
  links.find(l => l.id === "analytics"),
  links.find(l => l.id === "ai-compose"),
  links.find(l => l.id === "prices"),
  links.find(l => l.id === "hamburger"),
].filter(Boolean) as typeof links;

interface HeaderProps {
  isMobile?: boolean;
  onToggleSidebar?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ isMobile, onToggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSupportOpen, setIsSupportOpen] = React.useState(false);
  const [aiMobileMenuOpen, setAiMobileMenuOpen] = React.useState(false);

  return (
    <Container>
      {isMobile && aiMobileMenuOpen && (
        <AiMenuBackdrop
          onClick={() => setAiMobileMenuOpen(false)}
          aria-hidden
        />
      )}
      <Links>
        {(isMobile
          ? mobileLinks
          : links.filter(l => l.id !== "hamburger")
        ).map(link => (
          <Link
            key={link.id}
            variant="link1"
            $active={link.id !== "hamburger" && location.pathname === link.path}
            $disabled={link.disabled}
            $isMobile={!!isMobile}
            onClick={() => {
              if (link.disabled) return;
              if (isMobile && aiMobileMenuOpen && link.id !== "ai-compose") {
                setAiMobileMenuOpen(false);
              }
              if (link.id === "support") {
                setIsSupportOpen(true);
                return;
              }
              if (link.id === "ai-compose" && isMobile) {
                // Mobile: do not navigate immediately; open a small action menu above the button.
                setAiMobileMenuOpen((v) => !v);
                return;
              }
              if (link.id === "ai-compose" && location.pathname === Routes.AICompose) {
                if (isMobile && typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("ai-compose-open-modal"));
                }
                return;
              }
              if (link.id === "hamburger" && onToggleSidebar) {
                onToggleSidebar();
                return;
              }
              navigate(link.path);
            }}
          >
            {isMobile ? (
              <>
                {link.id === "" && <HomeIcon />}
                {link.id === "analytics" && <AnalyticsIcon />}
                {link.id === "ai-compose" && (
                  <MobileAiFabWrap>
                    {aiMobileMenuOpen && (
                      <MobileAiMenu onClick={(e) => e.stopPropagation()}>
                        <MobileAiMenuRow>
                          <MobileAiActionButton
                            type="button"
                            onClick={() => {
                              setAiMobileMenuOpen(false);
                              if (location.pathname === Routes.AICompose) {
                                if (typeof window !== "undefined") {
                                  window.dispatchEvent(new CustomEvent("ai-compose-open-modal"));
                                }
                              } else {
                                navigate(Routes.AICompose);
                              }
                            }}
                          >
                            <MobileAiActionText>
                              Generate
                            
                              music
                            </MobileAiActionText>
                          </MobileAiActionButton>
                          <MobileAiActionButton
                            type="button"
                            onClick={() => {
                              setAiMobileMenuOpen(false);
                              localStorage.setItem("type", "");
                              localStorage.setItem("newReleaseFrom", "releases");
                              navigate(`/releases/new-release?step=type`);
                            }}
                          >
                            <MobileAiActionText>
                              Create
                              a
                              release
                            </MobileAiActionText>
                          </MobileAiActionButton>
                        </MobileAiMenuRow>
                      </MobileAiMenu>
                    )}
                  <GradientIconWrapper>
                    <MusicIcon />
                  </GradientIconWrapper>
                  </MobileAiFabWrap>
                )}
                {link.id === "smart-links" && <ChainIcon />}
                {link.id === "prices" && <DollarIcon />}
                {link.id === "hamburger" && <HamburgerNavIcon />}
              </>
            ) : (
              link.title
            )}
          </Link>
        ))}
        <LanguageWrapper>
          <LanguageSelect />
        </LanguageWrapper>
      </Links>
      {!isMobile && (
        <Right>
          <DesktopCreateButton
            onClick={() => {
              localStorage.setItem("type", "");
              localStorage.setItem("newReleaseFrom", "releases");
              navigate(`/releases/new-release?step=type`);
            }}
          >
            <FormattedMessage
              id={`Header.Links.CreateRelease`}
              defaultMessage="Create a release"
            />
          </DesktopCreateButton>
          <Notification />
        </Right>
      )}
      <SupportModal open={isSupportOpen} onClose={() => setIsSupportOpen(false)} />
    </Container>
  );
};

const Container = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-radius: 20px;
  padding: 19px 19px 19px 34px;
  background-color: ${({ theme }) => theme.palette.background.primary};

  @media (max-width: ${DeviceSize.TabletMedium}) {
    padding: 0px 10px 0px 10px;
    border-radius: 16px;
    gap: 12px;
  }

  @media (max-width: 1500px) {
    padding: 10px;
  }
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
  flex: 1;
  flex-wrap: nowrap;

  @media (max-width: 1500px) {
      gap: 5px;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    flex: 1;
    justify-content: space-between;
    gap: 0;
  }
`;

const Link = styled(Typography) <{ $active: boolean; $disabled?: boolean; $isMobile?: boolean }>`
  padding: 0 5px;
  font-weight: 500;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props?.$disabled ? 'none' : 'auto'};
  cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
  user-select: none;
  display: flex;
  white-space: nowrap;
  flex-shrink: 0;

  &::before {
    content: "/";
    margin-right: 2px;
    font-weight: 1000;
    font-size: 18px;
    color: transparent;
    margin-bottom: 3px;
      flex-shrink: 0;
  }

  ${props => props.$active && `
    &::before {
      color: #6777FF;
    }
  `};

    @media (max-width: 1200px) {
        font-size: 14px;
        padding: 0 3px;

        &::before {
            font-size: 16px;
        }
    }

    @media (max-width: 1024px) {
        font-size: 13px;
        padding: 0 2px;

        &::before {
            font-size: 15px;
            margin-right: 1px;
        }
    }

    @media (max-width: 900px) {
        &:nth-child(5),
        &:nth-child(6)  {
            display: none;
        }
    }

    @media (max-width: 820px) {
        &:nth-child(4) {
            display: none;
        }
    }

    @media (max-width: ${DeviceSize.TabletMedium}) {
        font-size: 0;
        padding: 0 4px;

      &:nth-child(4),
      &:nth-child(5),
      &:nth-child(6) {
          display: flex;
      }
        
      &::before {
         display: none;
    }

    svg {
      width: 22px;
      height: 22px;
      stroke: currentColor;
    }
  }
`;

const Right = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;

    @media (max-width: 1500px) {
        gap: 10px;
    }

    @media (max-width: ${DeviceSize.TabletMedium}) {
        gap: 12px;
    }
`;

// Градиентный фон для иконки AI Music в мобильном нижнем баре
const GradientIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  padding: 10px;

  svg {
    width: 22px;
    height: 22px;
    fill: #ffffff;
  }

  @media (min-width: ${DeviceSize.TabletMedium}) {
    background: none;
    padding: 0;

    svg {
      width: 22px;
      height: 22px;
      fill: currentColor;
    }
  }
`;

const AiMenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 1;
`;

const MobileAiFabWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 6;
`;

const aiMenuIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px) scale(0.98);
    filter: blur(2px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
    filter: blur(0);
  }
`;

const aiButtonIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const MobileAiMenu = styled.div`
  position: absolute;
  left: 50%;
  bottom: calc(100% + 12px);
  transform: translateX(-50%);
  z-index: 1;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${aiMenuIn} 180ms cubic-bezier(0.2, 0.9, 0.2, 1) both;
  }
`;

const MobileAiMenuRow = styled.div`
  display: flex;
  gap: 12px;
`;

const MobileAiActionButton = styled.button`
  width: min(140px, calc((100vw - 80px) / 2));
  height: 56px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
  padding: 8px 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${aiButtonIn} 220ms cubic-bezier(0.2, 0.9, 0.2, 1) both;
  }

  &:nth-child(2) {
    @media (prefers-reduced-motion: no-preference) {
      animation-delay: 40ms;
    }
  }
`;

const MobileAiActionText = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
  text-align: center;
  color: #111827;
`;

const LanguageWrapper = styled.div`
  margin-left: 8px;
  flex-shrink: 0; /* Prevent language selector from shrinking */

  @media (max-width: 1200px) {
      margin-left: 4px;
  }

  @media (max-width: 1024px) {
      margin-left: 2px;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;

const DesktopCreateButton = styled(CreateReleaseButton)`
  white-space: nowrap; 
  flex-shrink: 0;

  @media (max-width: 1200px) {
      font-size: 14px;
      padding: 10px 16px;
  }

  @media (max-width: 1500px) {
      padding: 10px;
  }

  @media (max-width: 1024px) {
      font-size: 13px;
      padding: 8px 12px;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;

// Simple line icons for mobile nav (General, AI Music, Analytics, Smart Links, Prices, Menu)
const HomeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M4 11L12 4L20 11V20C20 20.5523 19.5523 21 19 21H5C4.44772 21 4 20.5523 4 20V11Z"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 21V14H14V21"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MusicIcon: React.FC = () => (
  <svg viewBox="0 0 720 720" fill="none">
    <path
      d="M566.058,16.271c-0.037-2.156-0.498-4.342-1.432-6.438c-3.724-8.36-13.518-12.118-21.883-8.396L214.115,147.802
        c-6.087,2.711-9.719,8.641-9.812,14.891c-0.014,0.998-0.017,271.821-0.017,271.821c-13.279-9.183-29.515-14.152-46.941-14.152
        c-27.781,0-56.079,12.364-77.637,33.92c-40.277,40.281-45.279,100.82-11.148,134.95c14.684,14.684,35.038,22.768,57.314,22.768
        c27.782,0,56.078-12.362,77.636-33.918c19.081-19.079,30.957-43.279,33.443-68.136c0.387-3.871,0.548-7.68,0.479-11.432
        c0.008-0.231,0-189.166,0-189.166l295.485-131.3v196.884c-13.278-9.183-29.512-14.152-46.939-14.152
        c-27.781,0-56.079,12.364-77.639,33.922c-40.28,40.281-45.28,100.82-11.147,134.949c14.684,14.684,35.036,22.769,57.312,22.769
        c27.781,0,56.079-12.362,77.636-33.918c22.489-22.492,34.094-51.296,33.92-78.293C566.132,439.562,566.076,17.321,566.058,16.271z
         M203.972,506.647c-1.726,17.267-10.214,34.314-23.899,47.999c-15.389,15.388-35.144,24.211-54.201,24.211
        c-9.495,0-23.083-2.266-33.878-13.061c-21.207-21.207-16.206-60.721,11.15-88.077c15.388-15.388,35.143-24.214,54.2-24.214
        c9.497,0,23.083,2.268,33.879,13.062C201.047,476.391,205.576,490.624,203.972,506.647z M237.43,273.079V173.7L532.915,42.098
        v99.682 M508.701,495.065c-15.388,15.387-35.141,24.21-54.2,24.21c-9.494,0-23.083-2.268-33.879-13.062
        c-21.207-21.207-16.204-60.719,11.15-88.076c15.391-15.389,35.144-24.214,54.203-24.214c9.497,0,23.08,2.266,33.876,13.062
        C541.062,428.192,536.059,467.704,508.701,495.065z"
      fill="#FFFFFF"
      stroke="#FFFFFF"
      strokeWidth="40"
      strokeLinejoin="round"
    />
  </svg>
);

const AnalyticsIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M4 20V10" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M10 20V4" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M16 20V8" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M3 20H21" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ChainIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M9 7L10.5 5.5C12.433 3.567 15.567 3.567 17.5 5.5C19.433 7.433 19.433 10.567 17.5 12.5L16 14"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15 17L13.5 18.5C11.567 20.433 8.433 20.433 6.5 18.5C4.567 16.567 4.567 13.433 6.5 11.5L8 10"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 14L14 10"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Dollar icon based on prices.svg asset (zoomed in to look larger in 24x24 box)
const DollarIcon: React.FC = () => (
  <svg viewBox="230 150 564 724" fill="none">
    <path
      d="M541.7 768v-45.3c46.3-2.4 81.5-15 108.7-37.8 27.2-22.8 40.8-53.1 40.8-88.2 0-37.8-11-65.7-35.3-83.4-24.6-20.1-59.8-35.4-111.6-45.3h-2.6V351.8c35.3 5.1 65.3 15 95.1 35.4l43.6-55.5c-43.6-27.9-89.9-42.9-138.8-45.3V256h-40.8v30.3c-40.8 2.4-76.3 15-103.5 37.8-27.2 22.8-40.8 53.1-40.8 88.2s11 63 35.3 80.7c21.7 17.7 59.8 32.7 108.7 42.9v118.5c-38.2-5.1-76.3-22.8-114.2-53.1l-48.9 53.1c48.9 40.5 103.5 63 163.3 68.1V768h41zm2.6-219.6c27.2 7.5 43.6 15 54.4 22.8 8.1 10.2 13.6 20.1 13.6 35.4s-5.5 25.2-19.1 35.4c-13.6 10.2-30.1 15-48.9 17.7V548.4zM449.2 440c-8.1-7.5-13.6-20.1-13.6-32.7 0-15 5.5-25.2 16.2-35.4 13.6-10.2 27.2-15 48.9-17.7v108.6c-27.2-7.8-43.4-15.3-51.5-22.8z"
      fill="currentColor"
    />
  </svg>
);

const HamburgerNavIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M4 7H20"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 12H20"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 17H20"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

